import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { EnrichUser } from './useEnrichUser';

const SIGNUP_CAPTURED_KEY = 'posthog_signup_captured';
const SIGNUP_WINDOW_MS = 60 * 1000; // 1 minute

export function usePostHogIdentity(user: EnrichUser | null) {
  const posthog = usePostHog();
  useEffect(() => {
    if (!user) {
      return;
    }

    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role,
    });

    // signed_up_at, not created_at: an admin-provisioned user's row can be months
    // older than the moment they actually joined, so gating on created_at silently
    // dropped this event for exactly that cohort (ENR-1760).
    const isRecentlySignedUp =
      user.signed_up_at !== null &&
      Date.now() - new Date(user.signed_up_at).getTime() < SIGNUP_WINDOW_MS;

    let alreadyCaptured = true;
    try {
      alreadyCaptured = localStorage.getItem(SIGNUP_CAPTURED_KEY) === user.id;
    } catch {
      console.warn('Unable to check for persisted signup event status - assuming already captured');
    }

    if (isRecentlySignedUp && !alreadyCaptured) {
      posthog.capture('user:signed_up');
      try {
        localStorage.setItem(SIGNUP_CAPTURED_KEY, user.id);
      } catch {
        console.warn('Unable to persist signup event status');
      }
    }
  }, [user, posthog]);

  return {
    reset: posthog.reset.bind(posthog),
  };
}
